import type { AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import axios from "axios";
import * as Sentry from "@sentry/nuxt";
import { useCurrentLocation } from "#imports";
import useCurrentOrganization from "~/composables/useCurrentOrganization";
import { API_ERROR_ORGANIZATION_CONTEXT_INVALID, API_ERROR_ORGANIZATION_CONTEXT_REQUIRED } from "~/constants/apiErrors";
import { ROUTE_SWITCHER } from "~/constants/routes";
import { allowlistedResponseFields } from "~/utils/failedRequest";
import type {
  FilterByQuery,
  IncludeQuery,
  PaginationQuery,
  PaginationResponse,
  Payload,
  SortByQuery,
} from "~/types/api";

declare module "axios" {
  interface AxiosRequestConfig {
    suppressValidationToast?: boolean;
    omitContextHeaders?: boolean;
    disableAuthRetry?: boolean;
  }
}

export interface ApiRequestHelpers {
  httpClient: AxiosInstance;
  getRequest: <Type>(url: string, config?: AxiosRequestConfig) => Promise<Type>;
  getPaginationRequest: <Type>(
    url: string,
    pagination?: PaginationQuery,
    sortBy?: SortByQuery,
    includes?: IncludeQuery,
    filterBy?: FilterByQuery,
    config?: AxiosRequestConfig,
  ) => Promise<PaginationResponse<Type>>;
  postRequest: <Type>(url: string, data?: Payload, config?: AxiosRequestConfig) => Promise<Type>;
  putRequest: <Type>(url: string, data?: Payload, config?: AxiosRequestConfig) => Promise<Type>;
  patchRequest: <Type>(url: string, data: Payload, config?: AxiosRequestConfig) => Promise<Type>;
  deleteRequest: <Type>(url: string, config?: AxiosRequestConfig) => Promise<Type>;
}

interface ContextHeaders {
  locationId: string | number | null | undefined;
  organizationId: string | null | undefined;
}

export function applyContextHeaders(
  requestConfig: InternalAxiosRequestConfig,
  { locationId, organizationId }: ContextHeaders,
): void {
  if (requestConfig.omitContextHeaders) {
    for (const key of Object.keys(requestConfig.headers)) {
      if (["locationid", "organizationid"].includes(key.toLowerCase())) {
        delete requestConfig.headers[key];
      }
    }
    return;
  }
  requestConfig.headers["LocationId"] = locationId;

  if (organizationId) {
    requestConfig.headers["OrganizationId"] = organizationId;
  }
}

/**
 * Matches the dedicated backend rejections for missing or invalid organization
 * context, and nothing else. Generic 400 and 403 responses must not wipe the
 * selected context.
 */
export function isOrganizationContextError(error: unknown): boolean {
  const data = (error as { response?: { data?: unknown } } | null | undefined)?.response?.data;

  if (typeof data !== "object" || data === null) {
    return false;
  }

  const errorCode = (data as { error?: unknown }).error;

  return errorCode === API_ERROR_ORGANIZATION_CONTEXT_INVALID || errorCode === API_ERROR_ORGANIZATION_CONTEXT_REQUIRED;
}

/** Avoid clearing a newly selected organization when an older request fails late. */
export function shouldRecoverFromOrganizationContextError(
  error: unknown,
  currentOrganizationId: string | null,
): boolean {
  if ((error as { config?: AxiosRequestConfig } | null)?.config?.omitContextHeaders) return false;
  if (!isOrganizationContextError(error)) {
    return false;
  }

  const failedOrganizationId = (error as { config?: { headers?: Record<string, unknown> } } | null | undefined)?.config
    ?.headers?.["OrganizationId"];

  return (failedOrganizationId ?? null) === currentOrganizationId;
}

/**
 * A stale organization context cannot recover on its own, since every request re-sends
 * the dead header. Drop the stored organization (which also drops the stored location
 * and its token, or the stale LocationId would immediately fail the same check) and
 * send the user back to the switcher to pick a live one.
 */
export async function recoverFromOrganizationContextError(
  forgetOrganization: () => Promise<void>,
  redirect: (path: string) => unknown,
): Promise<void> {
  await forgetOrganization();
  await redirect(ROUTE_SWITCHER);
}

export function suppressesValidationToast(requestConfig: AxiosRequestConfig | undefined): boolean {
  return requestConfig?.suppressValidationToast === true;
}

const cache = new Map<boolean, Promise<ApiRequestHelpers>>();

async function createHttpClient(showSentryCrashModalOnFailedRequest: boolean): Promise<ApiRequestHelpers> {
  const { public: config } = useRuntimeConfig();
  const { bearerToken, refreshAccessToken, signOut } = await useOAuth();
  const { currentLocationId } = useCurrentLocation();
  const { currentOrganizationId, syncCurrentOrganization, forgetOrganization } = useCurrentOrganization();

  const httpClient = axios.create({
    baseURL: config.api.baseUrl ?? "",
    headers: {
      common: {
        Accept: "application/json",
      },
    },
  });

  httpClient.interceptors.request.use(
    async (requestConfig: InternalAxiosRequestConfig) => {
      const { bearerToken, shouldRefreshToken, refreshAccessToken, user } = await useOAuth();

      if (!requestConfig.omitContextHeaders) syncCurrentOrganization(user.value);

      if (shouldRefreshToken()) {
        try {
          await refreshAccessToken();
        } catch (error) {
          console.error("[API] Proactive refresh failed:", error);
        }
      }

      const token = bearerToken();
      if (token) {
        requestConfig.headers["Authorization"] = token;
      }

      applyContextHeaders(requestConfig, {
        locationId: currentLocationId.value,
        organizationId: currentOrganizationId.value,
      });

      return requestConfig;
    },
    (error) => {
      return Promise.reject(error);
    },
  );

  httpClient.interceptors.response.use(
    (response: AxiosResponse): AxiosResponse => response,
    async (error) => {
      const originalRequest = error.config;
      const statusCode: number = error.response?.status;

      if (statusCode === 401 && originalRequest?.disableAuthRetry) {
        return Promise.reject(error);
      }

      // This happened when the route has return type blob for a file
      // but when we throw a validation exception that is also converted to blob
      // and we cannot get the error message from the blob
      if (error.response?.data instanceof Blob && error.response.data.type === "application/json") {
        try {
          const text = await error.response.data.text();
          const jsonData = JSON.parse(text);
          error.response.data = jsonData;
        } catch {
          // If parsing fails, keep the blob
        }
      }

      if (shouldRecoverFromOrganizationContextError(error, currentOrganizationId.value)) {
        if (import.meta.client) {
          await recoverFromOrganizationContextError(forgetOrganization, navigateTo);
        }

        return Promise.reject(error);
      }

      // Original request resulted in a 401 response, let's refresh the token and try again
      if (statusCode === 401 && !originalRequest._retry) {
        const token = bearerToken();
        if (!token) {
          return Promise.reject(error);
        }

        const refreshResult = await refreshAccessToken();

        if (!refreshResult) {
          return Promise.reject(error);
        }

        originalRequest._retry = true;
        originalRequest.headers["Authorization"] = bearerToken();

        return httpClient(originalRequest);
      }

      // 401 response after a retry should trigger a signOut
      if (statusCode === 401 && originalRequest._retry) {
        console.error("401 response after a retry should trigger a signOut", {
          statusCode,
          originalRequest: {
            url: originalRequest.url,
            method: originalRequest.method,
          },
        });
        await signOut();

        return true;
      }

      if (statusCode === 422 && import.meta.client) {
        if (!suppressesValidationToast(originalRequest)) {
          const nuxtApp = useNuxtApp();
          nuxtApp.runWithContext(() => {
            nuxtApp.$handleValidationError(error);
          });
        }

        return Promise.reject(error);
      }
      // This is a fallback, most statusCodes will be handled earlier/better than this.
      if (showSentryCrashModalOnFailedRequest && statusCode >= 400 && statusCode <= 599) {
        Sentry.captureException(error, {
          tags: {
            source: "api_request",
            status_code: statusCode.toString(),
          },
          extra: {
            url: originalRequest.url,
            method: originalRequest.method,
            ...allowlistedResponseFields(error.response?.data),
          },
        });

        // Skip toast in interceptor - useToast() triggers getActivePinia() before Pinia is ready.
        // Components handle errors via useQuery/useMutation error states.

        return Promise.reject(error);
      }

      return Promise.reject(error);
    },
  );

  const getRequest = async <Type>(url: string, config: AxiosRequestConfig = {}): Promise<Type> => {
    return httpClient.get(url, config).then((response: AxiosResponse) => {
      return response.data;
    });
  };

  const getPaginationRequest = async <Type>(
    url: string,
    pagination?: PaginationQuery,
    sortBy?: SortByQuery,
    includes?: IncludeQuery,
    filterBy?: FilterByQuery,
    config: AxiosRequestConfig = {},
  ): Promise<PaginationResponse<Type>> => {
    if (pagination) {
      // Combine pagination url parameters
      config.params = {
        ...config.params,
        ...{
          "page[number]": pagination.number,
          "page[size]": pagination.size,
        },
      };
    }

    if (sortBy) {
      const sortByFields = Array.isArray(sortBy) ? sortBy : [sortBy];
      let sortByQueryValue = "";

      for (sortBy of sortByFields) {
        // Add a multi value delimiter for sorting on multiple fields
        if (sortByQueryValue) {
          sortByQueryValue += ",";
        }

        // Add the sort direction
        if (sortBy.direction === "desc") {
          sortByQueryValue += "-";
        }

        // Add the sort column name
        sortByQueryValue += sortBy.column;
      }

      // Combine pagination url parameters
      config.params = {
        ...config.params,
        ...{
          sort: sortByQueryValue,
        },
      };
    }

    if (includes) {
      // Combine includes url parameters
      config.params = {
        ...config.params,
        ...{
          include: includes.join(","),
        },
      };
    }

    if (filterBy) {
      const filterParamsObject: Record<string, unknown> = {};

      filterBy.forEach((filter) => {
        if (filter.value && typeof filter.value === "object" && "from" in filter.value && "to" in filter.value) {
          const dateRange = filter.value as { from: string | null; to: string | null };
          if (dateRange.from) {
            filterParamsObject[`filter[${filter.column}][from]`] = dateRange.from;
          }
          if (dateRange.to) {
            filterParamsObject[`filter[${filter.column}][to]`] = dateRange.to;
          }
        } else if (Array.isArray(filter.value)) {
          filterParamsObject[`filter[${filter.column}]`] = filter.value.length === 0 ? "" : filter.value;
        } else {
          filterParamsObject[`filter[${filter.column}]`] = filter.value;
        }
      });

      config.params = {
        ...config.params,
        ...filterParamsObject,
      };
    }

    return httpClient
      .get(url, config)
      .then((response: AxiosResponse) => {
        return response.data;
      })
      .catch((error: Error) => {
        console.error(error);
        throw error;
      });
  };

  const postRequest = async <Type>(url: string, data?: Payload, config: AxiosRequestConfig = {}): Promise<Type> => {
    return httpClient.post(url, data, config).then((response: AxiosResponse) => response.data);
  };

  const putRequest = async <Type>(url: string, data?: Payload, config: AxiosRequestConfig = {}): Promise<Type> => {
    return httpClient.put(url, data, config).then((response: AxiosResponse) => response.data);
  };

  const patchRequest = async <Type>(url: string, data: Payload, config: AxiosRequestConfig = {}): Promise<Type> => {
    return httpClient.patch(url, data, config).then((response: AxiosResponse) => response.data);
  };

  const deleteRequest = async <Type>(url: string, config: AxiosRequestConfig = {}): Promise<Type> => {
    return httpClient.delete(url, config).then((response: AxiosResponse) => response.data);
  };

  return {
    httpClient,
    getRequest,
    getPaginationRequest,
    postRequest,
    putRequest,
    patchRequest,
    deleteRequest,
  };
}

export default async function useHttpClient(
  showSentryCrashModalOnFailedRequest: boolean = true,
): Promise<ApiRequestHelpers> {
  if (!cache.has(showSentryCrashModalOnFailedRequest)) {
    cache.set(showSentryCrashModalOnFailedRequest, createHttpClient(showSentryCrashModalOnFailedRequest));
  }
  return (await cache.get(showSentryCrashModalOnFailedRequest)) as ApiRequestHelpers;
}
